import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/Button/Button';
import { ROUTES } from '../../lib/constants';

import newImage from '../../images/Cityside-Fiber-2-Gigs.jpg';
import FormContainer5050 from '../../components/FormContainer5050/FormContainer5050';
import APIService from '../../lib/api.service';

import styles from './ContactUs.module.scss';

type InquiryType = 'customer' | 'general';

interface OutageComment {
  body: string;
  createdAt: string;
}

interface OutageTicket {
  ticketSubject: string;
  ticketComments: OutageComment[];
}

const formatOutageDate = (iso: string): string => {
  const d = new Date(iso);
  const pst = new Date(d.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const mm = String(pst.getMonth() + 1).padStart(2, '0');
  const dd = String(pst.getDate()).padStart(2, '0');
  const yy = String(pst.getFullYear()).slice(-2);
  let hours = pst.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const hh = String(hours).padStart(2, '0');
  const min = String(pst.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yy} ${hh}:${min}${ampm} PST`;
};

const ContactUs = (): React.JSX.Element => {
  const formRef = useRef<HTMLFormElement | null>(null);

  const [inquiryType, setInquiryType] = useState<InquiryType>('customer');
  const [showAnim, setShowAnim] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false);
  const [showNetworkError, setShowNetworkError] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [showReasonPrompt, setShowReasonPrompt] = useState(false);
  const [serverMessage, setServerMessage] = useState('');
  const [isDuplicateTicket, setIsDuplicateTicket] = useState(false);
  const [formData, setFormData] = useState<FormData>();

  const [outageData, setOutageData] = useState<OutageTicket[] | null>(null);
  const [showOutageModal, setShowOutageModal] = useState(false);

  useEffect(() => {
    APIService.getRecentOutageCommunication()
      .then(res => {
        if (!res.ok) return null;
        return res.json();
      })
      .then(json => {
        if (!json?.data?.length) return;
        const tickets = (json.data as OutageTicket[]).filter(
          t => Array.isArray(t.ticketComments) && t.ticketComments.length > 0
        );
        if (tickets.length) setOutageData(tickets);
      })
      .catch(() => {});
  }, []);

  const [reasonDropdownOpen, setReasonDropdownOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<{ value: string; label: string } | null>(null);
  const reasonDropdownRef = useRef<HTMLDivElement | null>(null);

  const reasonOptions = [
    { value: 'customer-support', label: 'Customer Support', desc: 'Questions about your account, billing, payments, or general service inquiries.' },
    { value: 'technical-support', label: 'Technical Support', desc: 'Issues with your internet connection, WiFi, speed, or equipment not working as expected.' },
    { value: 'installation-support', label: 'Installation Support', desc: 'Help with scheduling, rescheduling, or questions about your installation or activation.' },
  ];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (reasonDropdownRef.current && !reasonDropdownRef.current.contains(e.target as Node)) {
        setReasonDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onChange = () => {
    if (formRef.current) {
      setFormData(new FormData(formRef.current));
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setShowAnim(true);
    setShowEmailPrompt(false);

    const rawForm = formRef.current ? new FormData(formRef.current) : formData;
    if (!rawForm) {
      setShowAnim(false);
      setShowNetworkError(true);
      return;
    }

    const data: Record<string, string> = {};
    for (const pair of rawForm.entries()) {
      data[`${pair[0]}`] = `${pair[1]}`;
    }
    delete data['contacttype'];

    // Validate email if present
    if (data['email']) {
      const validEmail = new RegExp(/\S+@\S+\.\S+/);
      if (!validEmail.test(data['email'])) {
        setShowEmailPrompt(true);
        setShowAnim(false);
        return;
      }
    }

    let response;

    if (inquiryType === 'customer') {
      if (!selectedReason) {
        setShowReasonPrompt(true);
        setShowAnim(false);
        return;
      }
      data['type'] = selectedReason.label;
    }
    setShowReasonPrompt(false);

    try {
      if (inquiryType === 'customer') {
        response = await APIService.postTicket(JSON.stringify(data));
      } else {
        const generalData: Record<string, string> = {
          firstName: data['firstname'],
          lastName: data['lastname'],
          email: data['email'],
          description: data['description'],
        };
        if (data['phone']) generalData.phone = data['phone'];
        response = await APIService.postTicketInquiry(JSON.stringify(generalData));
      }
      const json = await response.json();

      if (json?.success) {
        setServerMessage(json.message ?? '');
        setShowForm(false);
        setShowSubmissionSuccess(true);
      } else {
        setServerMessage(json?.message ?? '');
        setIsDuplicateTicket(response.status === 409);
        setShowForm(false);
        setShowNetworkError(true);
      }
    } catch (error) {
      setShowForm(false);
      setShowNetworkError(true);
    } finally {
      setShowAnim(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{ROUTES.contactUs.title}</title>
        <meta
          name="description"
          content="Contact Cityside Fiber — submit a support ticket or general inquiry."
        />
        <meta property="og:title" content={`${ROUTES.contactUs.title}`} />
        <meta property="og:description" content="Contact Cityside Fiber — submit a support ticket or general inquiry." />
        <meta property="og:url" content="https://www.citysidefiber.com/contact-us" />
      </Helmet>

      {/* Outage Banner */}
      {outageData && outageData.length > 0 && (() => {
        const ticket = outageData[0];
        if (!ticket.ticketComments?.length) return null;
        const comments = [...ticket.ticketComments].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        const latest = comments[0];
        return (
          <>
            <div className={styles.outageBanner}>
              <div className={styles.outageBannerContent}>
                <strong className={styles.outageBannerSubject}>{ticket.ticketSubject}</strong>
                <span className={styles.outageBannerDate}>{formatOutageDate(latest.createdAt)}</span>
                <p className={styles.outageBannerBody}>{latest.body}</p>
                {comments.length > 1 && (
                  <button
                    type='button'
                    className={styles.outageBannerLink}
                    onClick={() => setShowOutageModal(true)}
                  >
                    Click or tap to view full history
                  </button>
                )}
              </div>
            </div>

            {/* Outage History Modal */}
            {showOutageModal && (
              <div className={styles.outageModalOverlay} onClick={() => setShowOutageModal(false)}>
                <div className={styles.outageModal} onClick={e => e.stopPropagation()}>
                  <div className={styles.outageModalHeader}>
                    <h3>{ticket.ticketSubject}</h3>
                    <button
                      type='button'
                      className={styles.outageModalClose}
                      onClick={() => setShowOutageModal(false)}
                      aria-label='Close'
                    >
                      &times;
                    </button>
                  </div>
                  <div className={styles.outageModalBody}>
                    {comments.map((c, i) => (
                      <div key={i} className={styles.outageModalEntry}>
                        <span className={styles.outageModalDate}>{formatOutageDate(c.createdAt)}</span>
                        <p className={styles.outageModalText}>{c.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {showForm &&
      <FormContainer5050
        imageD={newImage}
        imageM={newImage}
        imageDesc='Cityside Fiber Contact Us'
        loadImage='eager'
        hideDesignElement={true}
        loadingAnimationText='Submitting your request...'
        showLoadingAnimation={showAnim}
        imageFullHeight={true}
      >
        <form ref={formRef} onSubmit={onSubmit} onChange={onChange}>
          <h1 className='headline__h1'>
            <span className='color--dark-blue'>Contact Us</span>
          </h1>
          <p>How can we help you today?</p>

          {/* Radio Buttons */}
          <div className={styles.radioGroup}>
            <label className={styles.radioLabel}>
              <input
                type='radio'
                name='contacttype'
                value='customer'
                checked={inquiryType === 'customer'}
                onChange={() => setInquiryType('customer')}
              />
              <span>Customer</span>
            </label>
            <label className={styles.radioLabel}>
              <input
                type='radio'
                name='contacttype'
                value='general'
                checked={inquiryType === 'general'}
                onChange={() => setInquiryType('general')}
              />
              <span>General Inquiry</span>
            </label>
          </div>

          {/* Customer Fields */}
          {inquiryType === 'customer' && (
            <>
              <p className={styles.infoBlurb}>If you are a new or existing customer, please use the email associated with your MyCity account.</p>

              <div className='input-container'>
                <label htmlFor='email'>Email Address Linked to Your Account</label>
                <input
                  id='email'
                  name='email'
                  type='email'
                  placeholder=''
                  autoComplete='off'
                  required
                />
              </div>

              <div className='input-container'>
                <label>Reason for Contact</label>
                <div className={styles.customSelectContainer} ref={reasonDropdownRef}>
                  <button
                    type='button'
                    className={`${styles.customSelectTrigger} ${reasonDropdownOpen ? styles.open : ''}`}
                    onClick={() => setReasonDropdownOpen(o => !o)}
                    aria-haspopup='listbox'
                    aria-expanded={reasonDropdownOpen}
                  >
                    <span className={selectedReason ? '' : styles.triggerPlaceholder}>
                      {selectedReason ? selectedReason.label : 'Select the option that best matches...'}
                    </span>
                    <svg className={`${styles.triggerArrow} ${reasonDropdownOpen ? styles.triggerArrowOpen : ''}`} width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M12 16L6 9h12l-6 7z'/>
                    </svg>
                  </button>

                  {reasonDropdownOpen && (
                    <ul className={styles.customSelectDropdown} role='listbox'>
                      {reasonOptions.map(opt => (
                        <li
                          key={opt.value}
                          className={`${styles.customSelectOption} ${selectedReason?.value === opt.value ? styles.optionSelected : ''}`}
                          onClick={() => { setSelectedReason(opt); setReasonDropdownOpen(false); setShowReasonPrompt(false); }}
                          role='option'
                          aria-selected={selectedReason?.value === opt.value}
                        >
                          <span className={styles.customSelectOptionLabel}>{opt.label}</span>
                          <span className={styles.customSelectOptionDesc}>{opt.desc}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {showReasonPrompt && <p className={styles.errorText}>Please select a reason for contact.</p>}
              </div>

              <div className='input-container'>
                <label htmlFor='description'>Description</label>
                <textarea
                  id='description'
                  name='description'
                  rows={4}
                  placeholder='Please describe your issue or question...'
                  required
                />
              </div>
            </>
          )}

          {/* General Inquiry Fields */}
          {inquiryType === 'general' && (
            <>
              <p className={styles.infoBlurb}>Contact us with questions about Service Availability or to share input about Cityside Fiber and crews in your area.</p>

              <div className='multi-column-container'>
                <div className='input-container'>
                  <label htmlFor='firstname'>First Name</label>
                  <input
                    id='firstname'
                    name='firstname'
                    type='text'
                    autoComplete='off'
                    required
                  />
                </div>

                <div className='input-container'>
                  <label htmlFor='lastname'>Last Name</label>
                  <input
                    id='lastname'
                    name='lastname'
                    type='text'
                    autoComplete='off'
                    required
                  />
                </div>
              </div>

              <div className='multi-column-container'>
                <div className='input-container'>
                  <label htmlFor='email'>Email</label>
                  <input
                    id='email'
                    name='email'
                    type='email'
                    required
                  />
                </div>

                <div className='input-container'>
                  <label htmlFor='phone'>Phone (optional)</label>
                  <input
                    id='phone'
                    name='phone'
                    type='tel'
                    pattern='[0-9]{3}-?[0-9]{3}-?[0-9]{4}'
                    placeholder='XXX-XXX-XXXX'
                  />
                </div>
              </div>

              <div className='input-container'>
                <label htmlFor='description'>Description</label>
                <textarea
                  id='description'
                  name='description'
                  rows={4}
                  placeholder='Please describe your inquiry...'
                  required
                />
              </div>
            </>
          )}

          {showEmailPrompt &&
            <p className={styles.errorText}>Please enter a valid email address.</p>
          }

          <Button
            type='submit'
            className='btn--lg-extra-padding'
            size='md-to-lg'
            fullWidth={true}
          >
            Submit
          </Button>
        </form>
      </FormContainer5050>
      }

      {/* Success */}
      {showSubmissionSuccess &&
      <FormContainer5050
        imageD={newImage}
        imageM={newImage}
        imageDesc='Cityside Fiber Contact Us'
        loadImage='eager'
        hideDesignElement={true}
        imageFullHeight={true}
      >
        <form>
          <h1 className='headline__h1'>
            <span className='color--dark-blue'>Thanks for<br />reaching out!</span>
          </h1>
          {serverMessage
            ? <p style={{whiteSpace: 'pre-line'}}>{serverMessage}</p>
            : <p>We&#39;ve received your request. Our team will get back to you shortly.
                If you need immediate assistance, please email us at
                <a href="mailto:concierge@citysidefiber.com"> concierge@citysidefiber.com</a>.
              </p>
          }
        </form>
      </FormContainer5050>
      }

      {/* Network Error */}
      {showNetworkError &&
      <FormContainer5050
        imageD={newImage}
        imageM={newImage}
        imageDesc='Cityside Fiber Contact Us'
        loadImage='eager'
        hideDesignElement={true}
        imageFullHeight={true}
      >
        <form>
          <h2 className='headline__h2'>
            <span className='color--dark-blue'>
              {isDuplicateTicket ? 'Ticket found!' : <>Hmm, we&#39;re running<br />into a little trouble...</>}
            </span>
          </h2>
          {serverMessage
            ? <p style={{whiteSpace: 'pre-line'}}>{serverMessage}</p>
            : <p>It looks like we&#39;re having some issues submitting your request.
                Please try again later or reach out directly at
                <a href="mailto:concierge@citysidefiber.com"> concierge@citysidefiber.com</a>.
              </p>
          }
        </form>
      </FormContainer5050>
      }
    </>
  );
};

export default ContactUs;